import React from 'react';
import { useLocation } from 'react-router-dom';
import { LayoutContext } from 'contexts';
import Affix from '../Affix';
import { Text, Title } from '../Typography';
import Anchor from '../Anchor';
import Icon from '../Icon';
import { StyledAnchor, StyledItem } from './StyledComponents';

const { Link } = Anchor;

export type JumpToProps = {
  offsetTop?: number;
  targetOffset?: number;
  firstItemAlwaysActive?: boolean;
  hideBullets?: boolean;
  width?: number | string;
  getContainer?: () => HTMLElement;
  items?: {
    label: React.ReactText;
    id: string;
    checked?: boolean;
  }[];
};

export const JumpTo: React.FC<JumpToProps> = ({
  items,
  firstItemAlwaysActive,
  offsetTop,
  targetOffset,
  hideBullets,
  width = 'auto',
  getContainer,
}) => {
  const layout = React.useContext(LayoutContext);
  const location = useLocation();
  const [activeIds, setActiveIds] = React.useState(() => {
    if (!firstItemAlwaysActive) return [];
    return [items[0]?.id];
  });

  const handleChange = (currentActiveLink: string) => {
    const { pathname } = location;
    const id = currentActiveLink.replace(`${pathname}/#`, '');
    const lastIndex = items.findIndex(item => item.id === id);
    let newActive = items.slice(0, lastIndex + 1).map(item => item.id);
    if (firstItemAlwaysActive) newActive = [items[0]?.id, ...newActive];
    setActiveIds(newActive);
  };

  const findContainer = () => {
    if (getContainer) return getContainer;
    if (layout?.scrollContainer) return () => layout.scrollContainer;
    return null;
  };

  return (
    <Affix offsetTop={offsetTop}>
      <div>
        <Title level={3}>Jump to</Title>
        <StyledAnchor
          targetOffset={targetOffset}
          onClick={e => e.preventDefault()}
          onChange={handleChange}
          showInkInFixed={false}
          affix={false}
          getContainer={findContainer()}
        >
          {items.map(({ label, checked, id }) => (
            <Link
              href={`${location.pathname}/#${id}`}
              key={id}
              title={
                <StyledItem
                  data-cy={`sidepanel-${id}`}
                  $active={activeIds.includes(id)}
                >
                  {!hideBullets &&
                    (checked ? (
                      <Icon type="check" color="white" width={8} />
                    ) : (
                      <div className="unchecked"></div>
                    ))}
                  <Text>{label}</Text>
                </StyledItem>
              }
            />
          ))}
        </StyledAnchor>
      </div>
    </Affix>
  );
};

export default JumpTo;
